import { Bookmark, ChevronDown, ListFilter, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  useDeleteRulePreset,
  useFieldOptions,
  useRuleCatalog,
  useRulePresets,
  useSaveRulePreset,
} from '@/api/hooks'
import type { FieldOptions, RuleCatalog, RuleComparison, RuleValueKind } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { Input, NumberInput, Select } from '@/components/ui/Field'
import { EmptyState } from '@/components/ui/Feedback'
import { TokenSelect } from '@/components/ui/TokenSelect'
import { useToast } from '@/components/ui/toastContext'
import { foldTag } from '@/lib/tags'
import {
  defaultValue,
  describeRule,
  isComplete,
  newRule,
  parseRule,
  writeRules,
  type Rule,
} from './rules'

/**
 * The rule composer.
 *
 * Rules hide behind a toggle for the same reason the weights do: the
 * recommendations lead the screen, and constraints are something you reach for
 * rather than something you read past. The toggle carries a count, so the
 * rules in force are visible without opening the panel.
 *
 * Every row reads as a sentence, with the controls behind it rather than in it.
 * "At least one meal is vegetarian" is what you check at a glance; four
 * dropdowns are not.
 */
export function RulePanel({
  rules,
  onChange,
  open,
  onOpenChange,
  className,
}: {
  rules: Rule[]
  onChange: (next: Rule[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
}) {
  const catalog = useRuleCatalog()
  const [editing, setEditing] = useState<number | null>(null)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, onOpenChange])

  const data = catalog.data
  const rows = data ? rules.map((rule) => describeRule(rule, data)) : rules.map(() => null)

  return (
    <div ref={container} className={cn(className)}>
      <div className="col-start-1 row-start-1 flex flex-wrap items-center gap-2">
        <Button
          variant={rules.length > 0 ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-controls="recommendation-rules"
        >
          <ListFilter size={14} aria-hidden />
          Rules
          {rules.length > 0 ? (
            <span className="tabular ml-0.5 rounded-full bg-accent px-1.5 py-px text-micro text-on-accent">
              {rules.length}
            </span>
          ) : null}
        </Button>
      </div>

      {open ? (
        <div
          id="recommendation-rules"
          className="col-span-3 row-start-2 space-y-4 rounded-[var(--radius-card)] border border-line bg-surface p-4"
        >
          {rules.length === 0 && data ? (
            <EmptyState
              title="No rules"
              body="No rules set, so every recipe is considered."
            />
          ) : null}

          {catalog.isPending ? (
            <p className="text-micro text-ink-faint">Loading the rule catalogue…</p>
          ) : null}

          <ul className="space-y-1.5">
            {rules.map((rule, index) => (
              // The index is the rule's identity: a rule has no id of its own,
              // and keying on its text would rebuild the row -- and the editor
              // inside it -- on every keystroke.
              <li key={index}>
                {editing === index && data && rule ? (
                  <RuleEditor
                    rule={rule}
                    catalog={data}
                    onChange={(next) => {
                      const updated = [...rules]
                      updated[index] = next
                      onChange(updated)
                    }}
                    onDone={() => setEditing(null)}
                    onRemove={() => {
                      onChange(rules.filter((_, i) => i !== index))
                      setEditing(null)
                    }}
                  />
                ) : (
                  <RuleRow
                    summary={rows[index] ?? 'Unknown rule'}
                    incomplete={data ? !isComplete(rule, data) : false}
                    onEdit={() => setEditing(index)}
                    onRemove={() => {
                      onChange(rules.filter((_, i) => i !== index))
                      setEditing(null)
                    }}
                  />
                )}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (!data) return
                  onChange([...rules, newRule(data)])
                  setEditing(rules.length)
                }}
                disabled={!data}
              >
                <Plus size={14} aria-hidden />
                Add rule
              </Button>
              {rules.length > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Clear all rules"
                  onClick={() => {
                    onChange([])
                    setEditing(null)
                  }}
                >
                  Clear rules
                </Button>
              ) : null}
            </div>
            {data ? (
              <PresetControls
                catalog={data}
                rules={rules}
                onLoad={(loaded) => {
                  onChange(loaded.map(parseRule).filter((rule): rule is Rule => !!rule))
                  setEditing(null)
                }}
              />
            ) : null}
          </div>

          <p className="text-micro text-pretty text-ink-faint">
            A meal you pick by hand is exempt from the rules that would have
            hidden it, but its partners are held to all of them.
          </p>
        </div>
      ) : null}
    </div>
  )
}

// ----- one rule, read or edited -----

function RuleRow({
  summary,
  incomplete,
  onEdit,
  onRemove,
}: {
  summary: string | null
  /** A rule that is not finished yet is not applied, and says so. */
  incomplete: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  return (
    <div className="flex items-center gap-2 rounded-[var(--radius-field)] border border-line bg-surface-sunken px-3 py-2">
      <button
        type="button"
        onClick={onEdit}
        className="min-w-0 flex-1 text-left text-sm text-ink hover:text-ink-soft"
      >
        {summary ?? 'Unknown rule'}
        {incomplete ? (
          <span className="ml-1.5 text-micro text-caution">not applied yet</span>
        ) : null}
      </button>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onRemove}
        aria-label="Remove rule"
      >
        <Trash2 size={14} aria-hidden />
      </Button>
    </div>
  )
}

function RuleEditor({
  rule,
  catalog,
  onChange,
  onDone,
  onRemove,
}: {
  rule: Rule
  catalog: RuleCatalog
  onChange: (next: Rule) => void
  onDone: () => void
  onRemove: () => void
}) {
  const fieldOptions = useFieldOptions()
  const field =
    catalog.fields.find((entry) => entry.key === rule.field) ?? catalog.fields[0]
  const comparison =
    field?.comparisons.find((entry) => entry.key === rule.comparison) ??
    field?.comparisons[0]
  if (!field || !comparison) return null

  // Declared after the guard so `field` is already narrowed, rather than
  // re-checked inside every closure.
  const currentField = field
  const currentComparison = comparison

  return (
    <div className="space-y-2 rounded-[var(--radius-field)] border border-accent bg-surface p-3">
      <div className="grid gap-2 sm:grid-cols-3">
        <Select
          label="Field"
          value={rule.field}
          onChange={(event) => {
            const next = catalog.fields.find((entry) => entry.key === event.target.value)
            const first = next?.comparisons[0]
            if (next && first) {
              onChange(
                starter(
                  { field: next.key, comparison: first.key },
                  first,
                  optionsFor(fieldOptions.data, first.value_kind),
                ),
              )
            }
          }}
          options={catalog.fields.map((entry) => ({
            value: entry.key,
            label: entry.label,
          }))}
        />
        <Select
          label="Requirement"
          value={rule.comparison}
          onChange={(event) => {
            const next = currentField.comparisons.find(
              (entry) => entry.key === event.target.value,
            )
            if (next) {
              onChange(
                starter(
                  { field: currentField.key, comparison: next.key },
                  next,
                  optionsFor(fieldOptions.data, next.value_kind),
                ),
              )
            }
          }}
          options={currentField.comparisons.map((entry) => ({
            value: entry.key,
            label: entry.label,
          }))}
        />
        {currentComparison.scopes.length > 0 ? (
          <Select
            label="Applies to"
            value={rule.scope ?? currentComparison.scopes[0] ?? ''}
            onChange={(event) => onChange({ ...rule, scope: event.target.value })}
            options={currentComparison.scopes.map((scope) => ({
              value: scope,
              label: catalog.scope_labels[scope] ?? scope,
            }))}
          />
        ) : (
          <div className="flex items-end">
            <p className="pb-2 text-micro text-ink-faint">Compares the two meals</p>
          </div>
        )}
      </div>

      {rule.scope === 'combined' ? (
        <p className="text-micro text-ink-faint">
          Adds one serving of each meal, so the pair is judged as a whole.
        </p>
      ) : null}

      {currentComparison.value_kind !== 'none' ? (
        // Keyed on the field and the comparison, so that changing either starts
        // the value again rather than keeping text that belonged to the old one.
        <RuleValueField
          key={`${rule.field}:${currentComparison.key}`}
          rule={rule}
          comparison={currentComparison}
          onChange={(value) => onChange({ ...rule, value })}
        />
      ) : null}

      <p className="text-micro text-ink-faint">{describeRule(rule, catalog)}</p>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onRemove}>
          Remove
        </Button>
        <Button variant="secondary" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  )
}

/** Picks the scope and a starting value when the comparison changes. */
function starter(
  base: { field: string; comparison: string },
  comparison: RuleComparison,
  options: string[],
): Rule {
  if (comparison.scopes.length === 0) return { ...base }
  return {
    ...base,
    scope: comparison.scopes[0],
    value: defaultValue(comparison, options),
  }
}

/**
 * The vocabulary a value is drawn from.
 *
 * This is everything in use, never a subset of it: the other rules in force
 * have nothing to say about which values exist, and narrowing the list here
 * would leave it stale the moment a rule is removed and more recipes qualify.
 */
function optionsFor(
  options: FieldOptions | undefined,
  kind: RuleValueKind,
): string[] {
  if (!options) return []
  if (kind === 'recipe_cuisine') return options.cuisines
  if (kind === 'recipe_protein_base') return options.protein_bases
  if (kind === 'tag') return options.tags
  return []
}

const SEARCH_PLACEHOLDER: Record<string, string> = {
  recipe_cuisine: 'Search cuisines…',
  recipe_protein_base: 'Search protein bases…',
  tag: 'Search or add a tag…',
}

const VALUE_HINT: Record<string, string> = {
  recipe_cuisine: 'Only cuisines your recipes already use.',
  recipe_protein_base: 'Only protein bases your recipes already use.',
  tag: 'Anything you type becomes a tag, folded the way recipes store them.',
}

/** What to say about a value that is not in the collection. */
function unknownMessageFor(kind: RuleValueKind): ((term: string) => string) | undefined {
  if (kind === 'recipe_cuisine') return (term) => `No recipe uses the cuisine “${term}”.`
  if (kind === 'recipe_protein_base') {
    return (term) => `No recipe uses the protein base “${term}”.`
  }
  if (kind === 'tag') return (term) => `No recipe has the tag “${term}” yet.`
  return undefined
}

/** Whether a value is one the collection knows about. */
function isKnown(options: string[], kind: RuleValueKind, value: string): boolean {
  if (kind === 'tag') {
    const folded = foldTag(value)
    return options.some((option) => foldTag(option) === folded)
  }
  return options.includes(value)
}

/** "“A”, “B” or “C”", for the message about values nothing uses. */
function quoted(values: string[]): string {
  const parts = values.map((value) => `“${value}”`)
  if (parts.length === 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`
}

function RuleValueField({
  rule,
  comparison,
  onChange,
}: {
  rule: Rule
  comparison: RuleComparison
  onChange: (value: string) => void
}) {
  if (comparison.value_kind === 'boolean') {
    return (
      <Select
        label="Value"
        value={rule.value ?? 'true'}
        onChange={(event) => onChange(event.target.value)}
        options={[
          { value: 'true', label: 'vegetarian' },
          { value: 'false', label: 'not vegetarian' },
        ]}
      />
    )
  }

  if (
    comparison.value_kind === 'number' ||
    comparison.value_kind === 'days' ||
    comparison.value_kind === 'times' ||
    comparison.value_kind === 'decimal'
  ) {
    return (
      <NumberInput
        label="Value"
        value={rule.value === undefined || rule.value === '' ? null : Number(rule.value)}
        onValueChange={(value) => onChange(value === null ? '' : String(value))}
        min={comparison.min ?? 0}
        max={comparison.max ?? 9999}
        step={comparison.value_kind === 'decimal' ? 'any' : undefined}
        suffix={comparison.unit ?? UNIT[comparison.value_kind]}
      />
    )
  }

  return <RuleChoice rule={rule} comparison={comparison} onChange={onChange} />
}

const UNIT: Record<string, string | undefined> = {
  days: 'days',
  times: 'times',
  number: undefined,
}

/**
 * The value of a rule, picked from the collection.
 *
 * A free-text box was no use here: it offered "Italian, Mexican" whatever the
 * field was, and nothing said whether the value existed. The list is the
 * vocabulary of the field, the search finds a value in it, and a value that is
 * not in it is said out loud rather than quietly matching nothing.
 */
function RuleChoice({
  rule,
  comparison,
  onChange,
}: {
  rule: Rule
  comparison: RuleComparison
  onChange: (value: string) => void
}) {
  const fieldOptions = useFieldOptions()
  const kind = comparison.value_kind
  const options = optionsFor(fieldOptions.data, kind)
  const values = (rule.value ?? '').split('|').filter(Boolean)
  const unknown = values.filter((value) => !isKnown(options, kind, value))

  // A rule that needs a value and has none is not a rule the server will take.
  // It gets the first value on offer, once: the field above keys this control
  // on the field and the comparison, so a new comparison starts the value
  // again, while a value the user cleared stays cleared.
  const filled = useRef(false)
  useEffect(() => {
    if (filled.current) return
    const first = options[0]
    if (first === undefined) return
    filled.current = true
    if (values.length === 0) onChange(first)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, values.length])

  return (
    <TokenSelect
      label="Value"
      values={values}
      options={options}
      onChange={(next) => onChange(next.join('|'))}
      // A tag is stored folded, so that is how it is matched against as well.
      normalize={kind === 'tag' ? foldTag : undefined}
      // The vocabulary of a cuisine or a protein base is the collection's own;
      // a tag is the user's to grow.
      allowCustom={kind === 'tag'}
      unknownMessage={unknownMessageFor(kind)}
      searchPlaceholder={SEARCH_PLACEHOLDER[kind] ?? 'Search…'}
      hint={VALUE_HINT[kind]}
      warning={
        values.length === 0
          ? 'This rule needs a value before it applies.'
          : unknown.length > 0
            ? `No recipe uses ${quoted(unknown)}.`
            : undefined
      }
    />
  )
}

// ----- saved rule sets -----

/**
 * The saved rule sets.
 *
 * A row per set, with its own remove button on that row. The sets used to sit
 * side by side as pills with a bin button between them, which made it a guess
 * which set a bin belonged to.
 */
function PresetControls({
  catalog,
  rules,
  onLoad,
}: {
  catalog: RuleCatalog
  rules: Rule[]
  onLoad: (rules: string[]) => void
}) {
  const presets = useRulePresets()
  const save = useSaveRulePreset()
  const remove = useDeleteRulePreset()
  const toast = useToast()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const saved = presets.data ?? []

  async function forget(presetId: number, presetName: string) {
    setBusy(true)
    try {
      await remove.mutateAsync(presetId)
      toast.success(`Removed “${presetName}”.`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not remove that rule set.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <div ref={container} className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls="rule-presets"
        >
          <Bookmark size={13} aria-hidden />
          Saved rule sets
          {saved.length > 0 ? (
            <span className="tabular ml-0.5 rounded-full bg-accent px-1.5 py-px text-micro text-on-accent">
              {saved.length}
            </span>
          ) : null}
          <ChevronDown size={13} aria-hidden />
        </Button>

        {open ? (
          <div
            id="rule-presets"
            className="absolute top-full left-0 z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-[var(--radius-field)] border border-line bg-surface p-1 shadow-raised sm:right-0 sm:left-auto"
          >
            {saved.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-ink-faint">
                No rule sets saved yet.
              </p>
            ) : (
              <ul className="space-y-0.5">
                {saved.map((preset) => (
                  <li key={preset.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        onLoad(preset.rules)
                        setOpen(false)
                      }}
                      className="min-w-0 flex-1 rounded-md px-2 py-1.5 text-left text-sm text-ink hover:bg-surface-sunken"
                    >
                      <span className="block truncate">{preset.name}</span>
                      <span className="block text-micro text-ink-faint">
                        {preset.rules.length}{' '}
                        {preset.rules.length === 1 ? 'rule' : 'rules'}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove “${preset.name}”`}
                      title={`Remove “${preset.name}”`}
                      onClick={() => void forget(preset.id, preset.name)}
                      disabled={busy}
                    >
                      <Trash2 size={12} aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      <span className="flex items-center">
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Save as…"
          aria-label="Name for this rule set"
          wrapperClassName="w-36"
          className="h-8 py-1 text-micro"
        />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Save these rules"
          disabled={busy || name.trim() === ''}
          onClick={async () => {
            const trimmed = name.trim()
            if (!trimmed) return
            // A rule without a value is not a rule the server will store, so
            // the set is not saved half-made.
            if (rules.some((rule) => !isComplete(rule, catalog))) {
              toast.error('Give every rule a value before saving the set.')
              return
            }
            setBusy(true)
            try {
              await save.mutateAsync({
                name: trimmed,
                rules: writeRules(rules),
              })
              setName('')
              toast.success(`Saved “${trimmed}”.`)
            } catch (error) {
              toast.error(
                error instanceof Error ? error.message : 'Could not save that.',
              )
            } finally {
              setBusy(false)
            }
          }}
        >
          <Save size={13} aria-hidden />
        </Button>
      </span>
    </div>
  )
}